import type {
    ComponentCardPanelDef,
    ComponentCardRequiredPhotoDef,
    VehicleDamageCardConfig,
} from '../../types/questionnaire/definition';

/**
 * Default panel catalogue for the VEHICLE_DAMAGE_PICKER component card.
 * Panel ids are the stable contract shared with the car diagram SVG
 * (CarTopDownDiagram) — a replacement diagram asset must keep these ids.
 * Labels (and the panel subset) can be overridden per questionnaire via
 * CardDefComponent.componentConfig.panels.
 */
export const VEHICLE_DAMAGE_PANELS = [
    { id: 'front-bumper', label: 'Front bumper' },
    { id: 'bonnet', label: 'Bonnet' },
    { id: 'windscreen', label: 'Windscreen' },
    { id: 'roof', label: 'Roof' },
    { id: 'rear-window', label: 'Rear window' },
    { id: 'boot', label: 'Boot' },
    { id: 'rear-bumper', label: 'Rear bumper' },
    { id: 'left-mirror', label: 'Left wing mirror' },
    { id: 'left-front-guard', label: 'Left front guard' },
    { id: 'left-front-door', label: 'Left front door' },
    { id: 'left-rear-door', label: 'Left rear door' },
    { id: 'left-rear-quarter', label: 'Left rear quarter panel' },
    { id: 'right-mirror', label: 'Right wing mirror' },
    { id: 'right-front-guard', label: 'Right front guard' },
    { id: 'right-front-door', label: 'Right front door' },
    { id: 'right-rear-door', label: 'Right rear door' },
    { id: 'right-rear-quarter', label: 'Right rear quarter panel' },
] as const satisfies readonly ComponentCardPanelDef[];

export type VehicleDamagePanelId = (typeof VEHICLE_DAMAGE_PANELS)[number]['id'];

/**
 * Minimum photos per selected panel when the card's componentConfig doesn't
 * set minPhotosPerPanel: a selected panel needs at least one photo of the
 * damage. Authors opt back to prompted-only with an explicit 0.
 */
export const DEFAULT_MIN_PHOTOS_PER_PANEL = 1;

/**
 * Default always-required standalone photos for the VEHICLE_DAMAGE_PICKER
 * component card: every claim must include the odometer reading and VIN
 * plate regardless of which panels (if any) are damaged. Overridable per
 * questionnaire via CardDefComponent.componentConfig.requiredPhotos.
 */
export const VEHICLE_REQUIRED_PHOTOS = [
    { id: 'odometer-reading', label: 'Odometer reading' },
    { id: 'vin-plate', label: 'VIN plate' },
] as const satisfies readonly ComponentCardRequiredPhotoDef[];

/**
 * Resolves the always-required standalone photos for a vehicle damage picker
 * card. A configured list wins — including an explicit empty array, which
 * disables required photos; only an absent config falls back to the default
 * odometer reading + VIN plate pair.
 */
export function resolveVehicleRequiredPhotos(
    componentConfig?: VehicleDamageCardConfig,
): ComponentCardRequiredPhotoDef[] {
    return componentConfig?.requiredPhotos ?? [...VEHICLE_REQUIRED_PHOTOS];
}

/**
 * File categories the vehicle damage picker stamps on the claim photos it
 * uploads during lodgement: every photo carries DAMAGE_PHOTO_FILE_CATEGORY,
 * plus a per-panel `Damage: <panel label>` category. Shared because the claim's
 * Vehicle tab reads the same categories back to surface lodgement photos
 * alongside AI inspections — a drifted literal would silently hide them.
 */
export const DAMAGE_PHOTO_FILE_CATEGORY = 'Damage photo';
export const DAMAGE_PANEL_CATEGORY_PREFIX = 'Damage: ';

/**
 * Ravin's full triage set (confirmed by Ravin, 30 Sep 2026): 0 Total loss · 1 Repair ·
 * 2 Borderline repair · 3 Borderline total loss · 4 No damage · 5 Calculation in progress ·
 * 6 Needs attention · -1 Missing data. Only 0 to 4 are verdicts; -1, 5 and 6 mean the provider
 * could not assess the vehicle (CUR-1693). Every value is labelled so a handler sees the
 * provider's own outcome rather than a blank.
 */
export const VEHICLE_INSPECTION_TRIAGE_LABELS: Record<number, string> = {
    0: 'Total loss',
    1: 'Repair',
    2: 'Borderline repair',
    3: 'Borderline total loss',
    4: 'No damage',
    5: 'Calculation in progress',
    6: 'Needs attention',
    [-1]: 'Missing data',
};

/** Verdicts the claim may act on; the rest of the set says the provider could not assess the vehicle. */
export const VEHICLE_INSPECTION_FINAL_TRIAGE_VALUES: readonly number[] = [0, 1, 2, 3, 4];

/**
 * The provider's non-verdict triage values and what each one means for the handler (CUR-1693,
 * Danny at Ravin, 30 Sep 2026): -1 fires when year/make/model, odometer or valuation is missing;
 * 5 when the assessment has not finished on their side; 6 when their automatic assessment gave
 * up and queued the inspection for a person.
 */
export const VEHICLE_INSPECTION_UNASSESSED_TRIAGE = {
    [-1]: 'missing-data',
    5: 'calculation-in-progress',
    6: 'needs-attention',
} as const satisfies Record<number, string>;

export type VehicleInspectionUnassessedReason =
    (typeof VEHICLE_INSPECTION_UNASSESSED_TRIAGE)[keyof typeof VEHICLE_INSPECTION_UNASSESSED_TRIAGE];

/** Why the provider could not assess the vehicle, or null for a verdict, an unknown value or no triage. */
export function vehicleInspectionUnassessedReason(
    triage: number | null | undefined,
): VehicleInspectionUnassessedReason | null {
    if (triage === null || triage === undefined) {
        return null;
    }
    return (VEHICLE_INSPECTION_UNASSESSED_TRIAGE as Record<number, VehicleInspectionUnassessedReason>)[triage] ?? null;
}

/**
 * How long a claim-photo run may sit without a report before the Vehicle tab says so. Ravin
 * answers an image push within minutes when it answers at all, and never tells us when it will
 * not; one day is a product assumption, not a provider SLA — revisit once we have numbers.
 */
export const VEHICLE_INSPECTION_PHOTO_RUN_OVERDUE_MS = 24 * 60 * 60 * 1000;

/** Whether a triage value is a verdict the claim may act on; -1, 5, 6 and anything unknown are not. */
export function isVehicleInspectionTriageFinal(triage: number | null | undefined): triage is number {
    return triage !== null && triage !== undefined && VEHICLE_INSPECTION_FINAL_TRIAGE_VALUES.includes(triage);
}
